<template>
    <div>
        <template v-if="!loading">
            <div v-if="events.data.length" class="tw:flex tw:flex-col tw:gap-4">
                <EventoCard
                    v-for="event in events.data"
                    :key="event.id"
                    :evento="event"
                    :permisos="permisos"
                    :teatro="teatro"
                    :is-taquillero-visitante="isTaquilleroVisitante"
                    :expanded="expandedId === event.id"
                    :refresh-token="sesionesRefreshToken"
                    @cambiar-imagen="showModalCambiarImagen"
                    @toggle-sesiones="toggleSesiones"
                    @crear-sesion="showCrearSesion"
                    @editar-sesion="showModalEditarSesion"
                    @borrar-sesion="showModalBorrarSesion"
                    @copy-url="getUrlEvent"
                    @test-venta="openTestTab"
                    @descargar-plano="openDescargarPlano"
                    @borrar-evento="showModalBorrarEvento"
                />
            </div>
            <p v-else class="tw:py-8 tw:text-center tw:text-sm tw:text-slate-500">
                No se han encontrado eventos.
            </p>

            <DeleteEventModal
                v-for="event in events.data"
                :key="'del-' + event.id"
                :evento="event"
                v-on:close="closeModal"
            />

            <!-- Modales controlados por la sesión activa -->
            <CrearSesionModal v-if="active_event" :evento="active_event" v-on:close="closeModal"/>
            <EditarSesionModal v-if="active_sesion" :sesion="active_sesion" :evento="active_event" v-on:close="closeModal"/>
            <DeleteSesionModal v-if="active_sesion" :sesion="active_sesion" :evento="active_event" v-on:close="closeModal"/>
            <TestVentaInfoModal v-if="active_event && active_sesion" :evento="active_event" :sesion="active_sesion" @close="closeModal" />
            <DescargarPlanoPdf
                v-if="active_event && active_sesion"
                name="descargar-plano-pdf"
                :evento="active_event"
                :sesion_id="active_sesion.id"
                v-on:close="$modal.hide('descargar-plano-pdf')"
            />
            <DescargarInformeControlAcceso
                v-if="active_event && active_sesion"
                name="descargar-informe-control"
                :evento="active_event"
                :sesion_id="active_sesion.id"
                :taquillero_id="null"
                v-on:close="$modal.hide('descargar-informe-control')"
            />
            <CambiarImagenModal
                v-if="active_event"
                :evento="active_event"
                @close="closeModal"
                @updated="refreshEventos"
            />

            <TableFooter
                v-if="events.meta && events.meta.total > 0"
                class="tw:mt-6"
                :value="filters"
                :meta="events.meta"
                @input="onFooterInput"
            />
        </template>

        <!-- Skeleton mientras carga: misma rejilla que EventoCard (miniatura,
             pastillas de código/estado, título, acciones y footer Sesiones) -->
        <div v-else class="tw:flex tw:flex-col tw:gap-4" data-cy="eventos-skeleton">
            <Card v-for="n in 3" :key="'sk-' + n" :padding="0">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:xl:flex-row tw:xl:items-center tw:xl:justify-between tw:sm:p-5">
                    <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-4 tw:sm:flex-row tw:sm:items-start">
                        <!-- Miniatura del evento -->
                        <SkeletonItem height="tw:h-24 tw:sm:h-20" customClass="tw:w-full tw:sm:w-28 tw:shrink-0 tw:rounded-lg" />
                        <div class="tw:min-w-0 tw:space-y-2.5">
                            <!-- Metadatos: identidad + pastillas con la MISMA estructura
                                 que EventoCard. En móvil todo fluye en un único
                                 flex-wrap y cada pastilla rellena la primera línea;
                                 desde `sm` recuperan su caja y se agrupan. -->
                            <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5 tw:sm:gap-2">
                                <!-- Código + estado -->
                                <div class="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2">
                                    <SkeletonItem height="tw:h-5" customClass="tw:w-20 tw:rounded-full" />
                                    <SkeletonItem height="tw:h-5" customClass="tw:w-24 tw:rounded-full" />
                                </div>
                                <!-- Pastillas (recinto, compra privada...): disueltas en
                                     móvil para que fluyan, con caja propia desde `sm`. -->
                                <div class="tw:contents tw:sm:flex tw:sm:flex-wrap tw:sm:items-center tw:sm:gap-1.5">
                                    <SkeletonItem height="tw:h-5" customClass="tw:w-24 tw:rounded-full" />
                                    <SkeletonItem height="tw:h-5" customClass="tw:w-28 tw:rounded-full" />
                                </div>
                            </div>
                            <!-- Título del evento -->
                            <SkeletonItem height="tw:h-5" customClass="tw:w-64 tw:max-w-full tw:rounded" />
                            <!-- Contador de sesiones -->
                            <div class="tw:flex tw:flex-wrap tw:items-center">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-40 tw:rounded" />
                            </div>
                        </div>
                    </div>
                    <!-- Acciones a nivel de evento (en móvil, tras una línea fina) -->
                    <div class="tw:flex tw:flex-col tw:gap-1.5 tw:self-stretch tw:border-t tw:border-slate-200 tw:pt-3 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-center tw:sm:self-start tw:sm:border-0 tw:sm:pt-0 tw:xl:self-center">
                        <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-32" />
                        <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                        <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                        <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-24" />
                        <SkeletonItem height="tw:h-9" customClass="tw:w-full tw:rounded-sm tw:sm:w-28" />
                    </div>
                </div>
                <!-- Barra de sesiones -->
                <div class="tw:flex tw:w-full tw:items-center tw:justify-end tw:border-t tw:border-slate-200 tw:bg-slate-50 tw:px-4 tw:py-1.5">
                    <SkeletonItem height="tw:h-3.5" customClass="tw:w-3.5 tw:rounded" />
                </div>
            </Card>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue';
    import { useQueryList } from '@/composables/common/useQueryList';
    import { config } from '@/config.js';
    import store from '@/store';

    import Card from '@/modules/admin/ui/Card.vue';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue';
    import EventoCard from '@/modules/admin/eventos/components/EventoCard.vue';

    import DeleteEventModal from '@/views/backend/events/modals/DeleteEventModal';
    import DeleteSesionModal from '@/views/backend/events/modals/DeleteSesionModal';
    import CrearSesionModal from '@/views/backend/events/modals/CrearSesionModal';
    import EditarSesionModal from '@/views/backend/events/modals/EditarSesionModal';
    import CambiarImagenModal from '@/modules/admin/eventos/modals/CambiarImagenModal.vue';
    import DescargarInformeControlAcceso from '@/views/backend/events/modals/DescargarInformeControlAcceso';
    import DescargarPlanoPdf from '@/views/backend/events/modals/DescargarPlanoPdf.vue';
    import TestVentaInfoModal from '@/views/backend/events/modals/TestVentaInfoModal.vue';

    const instance = getCurrentInstance();
    const $modal = instance.proxy.$modal;
    const $toast = instance.proxy.$toast;

    const props = defineProps({
        search: {
            type: String,
            default: ''
        },
        pageResetToken: {
            type: Number,
            default: 0
        }
    });

    const emit = defineEmits(['changed']);

    const expandedId = ref(null);
    const active_event = ref(null);
    const active_sesion = ref(null);
    // Token que se incrementa tras crear/editar/borrar una sesión para que la
    // ficha expandida vuelva a pedir su desplegable al endpoint.
    const sesionesRefreshToken = ref(0);

    /**
     * Listado con filtros y paginación en la URL (`?search=&page=&per_page=`).
     * `useQueryList` hidrata desde `route.query`, debouncea la petición y
     * escribe la query al paginar o cambiar de tamaño de página. La `e`
     * distingue actuales/pasados en el backend y no se expone en la URL.
     */
    const {
        data: events,
        loading,
        filters,
        fetch: getEvents,
        updateUrlQuery,
        suppressNextUrlSync
    } = useQueryList({
        preserveQueryKeys: ['tab'],
        resetFiltersOnMount: props.pageResetToken > 0,
        clampPageToLastPage: true,
        flushUrlOnUnmount: false,
        fetcher: async({ filters: params }) => {
            const res = await store.dispatch(
                config.getVuexAlias('back.events.index'),
                { filters: { ...params, e: 'actuales' } }
            );

            return res.data || { data: [], meta: {} };
        },
        // El buscador ya debouncea 500 ms; sumar otra espera aquí se notaría.
        debounceMs: 0,
    });

    const teatro = computed(() => store.getters.getTeatro);
    const permisos = computed(() => store.getters.getPermisos);
    const footer = computed(() => store.getters.getFooter);
    const isTaquilleroVisitante = computed(() => store.getters.getUser.role === 11);

    function toggleSesiones(event) {
        expandedId.value = expandedId.value === event.id ? null : event.id;
    }

    /**
     * El pie de tabla emite el objeto { page, per_page, ... }. Se aplican sobre
     * `filters` y `useQueryList` relanza la petición y escribe la URL.
     */
    function onFooterInput(nextFilters) {
        const paginationChanged = filters.value.per_page !== nextFilters.per_page ||
            filters.value.page !== nextFilters.page;

        if (paginationChanged) suppressNextUrlSync();
        filters.value.per_page = nextFilters.per_page;
        filters.value.page = nextFilters.page;
        if (paginationChanged) updateUrlQuery();
    }

    /**
     * Refresco tras crear/editar/borrar sin pasar por `loading`: el listado no
     * debe desmontar las fichas (la expandida perdería su panel de sesiones).
     * Avisa al padre para que vuelva a pedir los totales de las pestañas.
     */
    function refreshEventos() {
        emit('changed');
        getEvents(undefined, { silent: true });
    }

    function showModal(modal) {
        expandedId.value = null;
        $modal.show(modal);
    }

    /**
     * Cambios que afectan al desplegable de sesiones (crear, editar, borrar).
     * Se detectan por el nombre del modal de origen.
     */
    function esCambioDeSesion(modal) {
        return typeof modal === 'string' && (
            modal === 'crear-sesion' ||
            modal.startsWith('editar-sesion-') ||
            modal.startsWith('borrar-sesion-')
        );
    }

    function closeModal(modal, changed) {
        active_sesion.value = null;
        active_event.value = null;
        $modal.hide(modal);
        if (changed) {
            // El desplegable de la ficha no se remonta al recargar eventos
            // (el listado no vuelve a loading), así que se fuerza su refetch
            // con el token. El listado de eventos se recarga para refrescar el
            // contador de sesiones.
            if (esCambioDeSesion(modal)) sesionesRefreshToken.value++;
            refreshEventos();
        }
    }

    async function getUrlEvent(sesion, event) {
        let url = '';
        if (event.slug) {
            url = process.env.VUE_APP_CLE_FRONT + 'event/' + event.slug;
        } else {
            url = footer.value.url_gracias + 'eventos/' + event.id + '/' + sesion.id;
        }

        await navigator.clipboard.writeText(url);
        $toast.success('Url copiada!');
    }

    function openTestTab(sesion, event) {
        active_event.value = event;
        active_sesion.value = sesion;
        nextTick(() => {
            $modal.show('test-venta-warning-' + event.id + '-' + sesion.id);
        });
    }

    function openDescargarPlano(sesion, event) {
        active_event.value = event;
        active_sesion.value = sesion;
        nextTick(() => {
            $modal.show('descargar-plano-pdf');
        });
    }

    function showModalEditarSesion(sesion, event) {
        active_sesion.value = sesion;
        active_event.value = event;
        nextTick(() => {
            $modal.show('editar-sesion-' + sesion.id);
        });
    }

    function showModalBorrarSesion(sesion, event) {
        active_sesion.value = sesion;
        active_event.value = event;
        nextTick(() => {
            $modal.show('borrar-sesion-' + sesion.id);
        });
    }

    function showCrearSesion(event) {
        active_event.value = event;
        nextTick(() => {
            $modal.show('crear-sesion');
        });
    }

    function showModalCambiarImagen(evento) {
        active_event.value = evento;
        nextTick(() => {
            $modal.show('cambiar-imagen-' + evento.id);
        });
    }

    function showModalBorrarEvento(evento) {
        showModal('borrar-' + evento.id);
    }

    // Un clic explícito sobre la pestaña activa también debe recargar el
    // listado, aunque ya esté en página 1 y no tenga búsqueda.
    watch(() => props.pageResetToken, () => {
        const filtersNeedReset = filters.value.page !== 1 || Boolean(filters.value.search);

        if (filters.value.page !== 1) {
            suppressNextUrlSync();
            filters.value.page = 1;
        }
        if (filters.value.search) filters.value.search = '';

        if (!filtersNeedReset) getEvents();
    });
    // La búsqueda del padre es la fuente del término; al cambiar, `filters`
    // dispara el fetch y el reset a la primera página dentro de useQueryList.
    watch(() => props.search, val => {
        if (val !== filters.value.search) {
            filters.value.search = val;
        }
    });
</script>
